import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import type {
  DataRegion,
  MeIssueCount,
  MeMembership,
  PartnerContext,
  Team,
  User,
} from './team.types';

type SetTeamId = (value: string | null | undefined) => void;

export const userContext = createContext<User>(undefined as any);

export const teamIdContext = createContext<{
  teamId: string | undefined | null;
  storedTeamId: string | undefined | null;
  setTeamId: SetTeamId;
}>({
  teamId: undefined,
  storedTeamId: undefined,
  setTeamId: () => undefined,
});

export const useTeamIdContext = () => useContext(teamIdContext);

/**
 * Session team state resolved from the `/me` bootstrap:
 * - `unresolved`: `/me` has not settled for the requested team yet.
 * - `teamless`: `/me` settled without a team for this user.
 * - `resolved`: `team` is hydrated and non-null.
 */
export type TeamState = 'unresolved' | 'teamless' | 'resolved';

export type TeamContextValue = {
  teamId: string;
  setTeamId: SetTeamId;
  teamState: TeamState;
  team: Team | null;
  partnerContext: PartnerContext | null;
  selectedDataRegion: DataRegion | null;
  setSelectedDataRegion: (dataRegion: DataRegion) => Promise<void>;
  membership: MeMembership | null;
  features: string[];
  issues: MeIssueCount[];
  /** VRI client teams keep legacy roles; every other team uses UI Permission Roles (PRO-19834). */
  usesUiPermissionRoles: boolean;
  isTeamAdmin: boolean;
  isPartnerAdmin: boolean;
  isVrpAdmin: boolean;
  isSuperAdmin: boolean;
  isFetchingTeam: boolean;
  queryError?: Error | null;
  refetchTeam: () => Promise<void>;
  queryKey: readonly unknown[];
};

export const teamContext = createContext<TeamContextValue>({
  isTeamAdmin: false,
  isPartnerAdmin: false,
  isVrpAdmin: false,
  isSuperAdmin: false,
  teamState: 'unresolved',
  team: null,
  partnerContext: null,
  selectedDataRegion: null,
  setSelectedDataRegion: async () => undefined,
  membership: null,
  features: [],
  issues: [],
  usesUiPermissionRoles: false,
  teamId: '',
  isFetchingTeam: false,
  queryError: null,
  setTeamId: () => undefined,
  refetchTeam: async () => undefined,
  queryKey: [],
});

type MeSessionQueryResult = {
  data:
    | {
        user: User;
        team: Team | null;
        partnerContext: PartnerContext | null;
        membership: MeMembership | null;
        features: string[];
        issues: MeIssueCount[];
      }
    | undefined;
  error: Error | null;
  isFetching: boolean;
  queryKey: readonly unknown[];
  refetch: () => Promise<unknown>;
};

export function MeSessionContextProvider({
  children,
  isBootstrappingTeamId,
  me,
  realUserId,
  selectedDataRegion = null,
  setSelectedDataRegion = async () => undefined,
  setTeamId,
}: {
  children: ReactNode;
  isBootstrappingTeamId: boolean;
  me: MeSessionQueryResult;
  realUserId: string;
  selectedDataRegion?: DataRegion | null;
  setSelectedDataRegion?: (dataRegion: DataRegion) => Promise<void>;
  setTeamId: SetTeamId;
}) {
  const meUser = me.data?.user;
  const team = me.data?.team ?? null;
  const partnerContext = me.data?.partnerContext ?? null;
  const membership = me.data?.membership ?? null;
  const isTeamAdmin =
    !!meUser &&
    (meUser.isSuperAdmin ||
      meUser.isVrpAdmin ||
      meUser.isPartnerAdmin ||
      membership?.role === 'admin');
  const user = useMemo(
    () =>
      meUser
        ? {
            ...meUser,
            realUserId,
            canAccessTeam: isTeamAdmin || !!membership,
            isTeamAdmin,
          }
        : undefined,
    [meUser, realUserId, isTeamAdmin, membership]
  );
  const refetch = useCallback(async () => {
    await me.refetch();
  }, [me]);
  const isFetchingTeam = isBootstrappingTeamId || (me.isFetching && !me.data);
  const teamValue = useMemo(
    () => ({
      teamId: team?.id ?? '',
      setTeamId,
      teamState: me.data
        ? team
          ? ('resolved' as const)
          : ('teamless' as const)
        : ('unresolved' as const),
      team,
      partnerContext,
      selectedDataRegion,
      setSelectedDataRegion,
      membership,
      features: me.data?.features ?? [],
      issues: me.data?.issues ?? [],
      usesUiPermissionRoles:
        !!team && (team.type !== 'propertyManager' || team.isGL),
      isSuperAdmin: meUser?.isSuperAdmin ?? false,
      isVrpAdmin: meUser?.isVrpAdmin ?? false,
      isPartnerAdmin: meUser?.isPartnerAdmin ?? false,
      isTeamAdmin,
      refetchTeam: refetch,
      isFetchingTeam,
      queryError: me.error,
      queryKey: me.queryKey,
    }),
    [
      team,
      partnerContext,
      selectedDataRegion,
      setSelectedDataRegion,
      setTeamId,
      me.data,
      me.error,
      me.queryKey,
      membership,
      meUser,
      isTeamAdmin,
      refetch,
      isFetchingTeam,
    ]
  );

  return (
    <userContext.Provider value={user!}>
      <teamContext.Provider value={teamValue}>{children}</teamContext.Provider>
    </userContext.Provider>
  );
}
